import { computed, ref } from "vue";
import { useI18n } from "vue-i18n";
import { useAppToast } from "@/composables/useAppToast.js";
import { useCustomerReviewApi } from "@/composables/model/customerReview/useCustomerReviewApi.js";
import { useCustomerReviewStore } from "@/stores/customerReview.js";
import {
  buildAddCustomerReviewPayload,
  buildUpdateCustomerReviewPayload,
} from "@/utils/customerReview.js";

export function useCustomerReview() {
  const store = useCustomerReviewStore();

  const {
    getCustomerReviews,
    addCustomerReview,
    updateCustomerReview,
    deleteCustomerReview,
  } = useCustomerReviewApi();

  const { toastSuccess, toastError } = useAppToast();
  const { t } = useI18n();
  const deletingId = ref(null);
  const saving = ref(false);

  const reviews = computed(() => store.reviews);

  async function loadReviews() {
    const result = await getCustomerReviews();
    if (result.success) {
      store.setReviews(Array.isArray(result.data) ? result.data : []);
    }
    return result;
  }

  async function createReview(form) {
    if (saving.value)
      return {
        success: false,
        data: null,
        message: t("customerSession.api.saveError"),
      };
    saving.value = true;
    try {
      const result = await addCustomerReview(
        buildAddCustomerReviewPayload(form),
      );
      if (result.success) {
        toastSuccess(result.message);
        const reloaded = await loadReviews();
        if (!reloaded.success) toastError(reloaded.message);
      } else {
        toastError(result.message);
      }
      return result;
    } finally {
      saving.value = false;
    }
  }

  async function updateReview(form) {
    if (saving.value)
      return {
        success: false,
        data: null,
        message: t("customerSession.api.saveError"),
      };
    saving.value = true;
    try {
      const result = await updateCustomerReview(
        buildUpdateCustomerReviewPayload(form),
      );
      if (result.success) {
        toastSuccess(result.message);
        const reloaded = await loadReviews();
        if (!reloaded.success) toastError(reloaded.message);
      } else {
        toastError(result.message);
      }
      return result;
    } finally {
      saving.value = false;
    }
  }

  async function removeReview(id) {
    if (deletingId.value !== null)
      return {
        success: false,
        data: null,
        message: t("customerSession.api.deleteError"),
      };
    deletingId.value = id;
    try {
      const result = await deleteCustomerReview(id);
      if (result.success) {
        toastSuccess(result.message);
        const reloaded = await loadReviews();
        if (!reloaded.success) toastError(reloaded.message);
      } else {
        toastError(result.message);
      }
      return result;
    } finally {
      deletingId.value = null;
    }
  }

  return {
    reviews,
    deletingId,
    saving,
    loadReviews,
    createReview,
    updateReview,
    removeReview,
  };
}
